import { Injectable } from '@angular/core';
import { BulkUpdateValidationError, BulkUploadValidationErrorType } from '@app/models/bulk-update/bulk-update.model';
import { RoleName } from '@app/models/user-role.model';
import { User } from '@app/models/user/user.model';
import moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class BulkUpdateValidationService {
  private readonly REGEX_EMAIL: RegExp = /^(.*).@(.*).\..+$/;

  constructor() { }

  validateFirstName(value: string): (BulkUpdateValidationError | null) {
    if (value.length === 0) {
      return new BulkUpdateValidationError('First name is required', BulkUploadValidationErrorType.REQUIRED);
    }

    if (value.length > 100) {
      return new BulkUpdateValidationError('First name cannot be more than 100 characters', BulkUploadValidationErrorType.MAX_LENGTH);
    }

    return null;
  }

  validateLastName(value: string): (BulkUpdateValidationError | null) {
    if (value.length === 0) {
      return new BulkUpdateValidationError('Last name is required', BulkUploadValidationErrorType.REQUIRED);
    }

    if (value.length > 100) {
      return new BulkUpdateValidationError('Last name cannot be more than 100 characters', BulkUploadValidationErrorType.MAX_LENGTH);
    }

    return null;
  }

  // TODO: Maybe merge these two functions?
  validateEmail(value: string, existingUsers: User[] = [], otherValues: string[] = []): (BulkUpdateValidationError | null) {
    if (value.length === 0) {
      return new BulkUpdateValidationError('Email is required', BulkUploadValidationErrorType.REQUIRED);
    }

    if (value.length > 100) {
      return new BulkUpdateValidationError('Email cannot be more than 100 characters', BulkUploadValidationErrorType.MAX_LENGTH);
    }

    if (!this.REGEX_EMAIL.test(value)) {
      return new BulkUpdateValidationError('Not a valid email address', BulkUploadValidationErrorType.EMAIL_INVALID_FORMAT);
    }

    if (!existingUsers.some(u => u.email === value)) {
      return new BulkUpdateValidationError('A user does not exist for this email address', BulkUploadValidationErrorType.EMAIL_INVALID_NOT_FOUND);
    }

    if (otherValues.filter(v => v === value).length > 1) {
      return new BulkUpdateValidationError('This email address appears in multiple rows, email address can only appear once per file', BulkUploadValidationErrorType.EMAIL_INVALID_DUPLICATE_FORM);
    }

    return null;
  }

  validateManagerEmail(value: string, existingUsers: User[] = []): (BulkUpdateValidationError | null) {
    // Optional field so only validate if filled in
    if (value.length > 0) {
      if (value.length > 100) {
        return new BulkUpdateValidationError('Manager email cannot be more than 100 characters', BulkUploadValidationErrorType.MAX_LENGTH);
      }

      if (!this.REGEX_EMAIL.test(value)) {
        return new BulkUpdateValidationError('Not a valid email address', BulkUploadValidationErrorType.EMAIL_INVALID_FORMAT);
      }

      if (!existingUsers.some(u => u.email === value)) {
        return new BulkUpdateValidationError('A user does not exist for this email address', BulkUploadValidationErrorType.EMAIL_INVALID_NOT_FOUND);
      }
    }

    return null;
  }

  validateStartDate(value: string): (BulkUpdateValidationError | null) {
    if (value.length === 0) {
      return null;
    }

    if (!moment(value, 'DD/MM/YYYY', true).isValid()) {
      return new BulkUpdateValidationError('Start date must be in the format dd/MM/yyyy (e.g. 31/12/1999)', BulkUploadValidationErrorType.START_DATE_INVALID_FORMAT);
    }

    return null;
  }

  validateDepartment(value: string): (BulkUpdateValidationError | null) {
    if (value.length > 100) {
      return new BulkUpdateValidationError('Department cannot be more than 100 characters', BulkUploadValidationErrorType.MAX_LENGTH);
    }

    return null;
  }

  validateRole(value: string): (BulkUpdateValidationError | null) {
    if (value.length === 0) {
      return new BulkUpdateValidationError('Role is required', BulkUploadValidationErrorType.REQUIRED);
    }

    if (value !== RoleName.USER && value !== RoleName.MANAGER && value !== RoleName.ADMIN) {
      return new BulkUpdateValidationError('This is not a valid role name', BulkUploadValidationErrorType.ROLE_INVALID_NOT_FOUND);
    }

    return null;
  }

  validatePosition(value: string): (BulkUpdateValidationError | null) {
    if (value.length === 0) {
      return null;
    }

    if (value.length > 100) {
      return new BulkUpdateValidationError('Position cannot be more than 100 characters', BulkUploadValidationErrorType.MAX_LENGTH);
    }

    return null;
  }

  validateGrade(value: string): (BulkUpdateValidationError | null) {
    // Optional field so only validate if filled in
    if (value.length > 100) {
      return new BulkUpdateValidationError('Grade cannot be more than 100 characters', BulkUploadValidationErrorType.MAX_LENGTH);
    }

    return null;
  }
}
